import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import jsQR from 'jsqr';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';

// ─── Escáner de QR para el portal de despacho ──────────────────────────────────
// Usa jsQR + getUserMedia para funcionar en Chrome Android, iOS Safari y cualquier
// navegador moderno. No depende de BarcodeDetector.

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.overlayBackdropStrong};
  z-index: 300;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Marco = styled.div`
  position: relative;
  width: min(86vw, 360px);
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.cameraBg};
  border: 2px solid ${({ theme }) => theme.colors.primary};
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CanvasOculto = styled.canvas`
  display: none;
`;

const Mira = styled.div`
  position: absolute;
  inset: 18%;
  border: 2px solid ${({ theme }) => theme.colors.onPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 0 0 9999px ${({ theme }) => theme.colors.overlaySpotlight};
`;

const Texto = styled.p`
  color: ${({ theme }) => theme.colors.onPrimary};
  font-size: 0.9rem;
  text-align: center;
  margin: 16px 0 0;
  max-width: 360px;
`;

const Acciones = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 18px;
  width: min(86vw, 360px);
`;

const Boton = styled.button`
  flex: 1;
  min-height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme, $primary }) => ($primary ? theme.colors.primary : theme.colors.onPrimary)};
  background: ${({ theme, $primary }) => ($primary ? theme.colors.primary : 'transparent')};
  color: ${({ theme }) => theme.colors.onPrimary};
  font-size: 0.92rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition-property: opacity, background-color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  &:hover { opacity: 0.85; }
`;

const ManualWrap = styled.div`
  width: min(86vw, 360px);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ManualLabel = styled.p`
  color: ${({ theme }) => theme.colors.onPrimary};
  font-size: 0.9rem;
  text-align: center;
  margin: 0 0 4px;
`;

const ManualInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 1rem;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.shadowFocus};
  }
`;

const esHttpPublico = () =>
  typeof window !== 'undefined' &&
  window.location.protocol === 'http:' &&
  window.location.hostname !== 'localhost' &&
  window.location.hostname !== '127.0.0.1';

export default function EscanerQR({ onDetectar, onCerrar }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const detenidoRef = useRef(false);

  const [modoManual, setModoManual] = useState(false);
  const [errorCamara, setErrorCamara] = useState('');
  const [manual, setManual] = useState('');

  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();

  const detener = useCallback(() => {
    detenidoRef.current = true;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (modoManual) return undefined;

    // Chrome Android requiere HTTPS para getUserMedia incluso en red local.
    if (esHttpPublico()) {
      setErrorCamara('La cámara requiere HTTPS. Accedé al portal por https:// o desde la red de producción.');
      setModoManual(true);
      return undefined;
    }

    detenidoRef.current = false;

    const iniciar = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        // Si el componente se desmontó mientras esperábamos el permiso, liberar inmediatamente.
        if (detenidoRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        escanear();
      } catch (err) {
        const msg = err?.name === 'NotAllowedError'
          ? 'Permiso de cámara denegado. Habilitalo en la configuración del navegador.'
          : err?.name === 'NotFoundError'
          ? 'No se encontró ninguna cámara en este dispositivo.'
          : `No se pudo acceder a la cámara (${err?.name || 'error desconocido'}).`;
        setErrorCamara(msg);
        setModoManual(true);
      }
    };

    const escanear = () => {
      const loop = () => {
        if (detenidoRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || video.readyState < 2) {
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const codigo = jsQR(imageData.data, imageData.width, imageData.height);
        if (codigo) {
          detener();
          onDetectar(codigo.data);
          return;
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    };

    iniciar();
    return () => detener();
  }, [modoManual, onDetectar, detener]);

  const enviarManual = () => {
    const v = manual.trim();
    if (v) {
      detener();
      onDetectar(v);
    }
  };

  const cerrarDesdeBackdrop = buildOnBackdropClick(() => { detener(); onCerrar(); });

  return (
    <Overlay onClick={cerrarDesdeBackdrop} onMouseDown={onBackdropMouseDown}>
      {!modoManual ? (
        <>
          <Marco>
            <Video ref={videoRef} playsInline muted />
            <CanvasOculto ref={canvasRef} />
            <Mira />
          </Marco>
          <Texto>Apuntá la cámara al código QR del equipo.</Texto>
          <Acciones>
            <Boton type="button" onClick={() => { detener(); setModoManual(true); }}>
              Ingresar manualmente
            </Boton>
            <Boton type="button" onClick={() => { detener(); onCerrar(); }}>Cancelar</Boton>
          </Acciones>
        </>
      ) : (
        <ManualWrap>
          {errorCamara ? (
            <ManualLabel style={{ fontSize: '0.82rem', opacity: 0.85, lineHeight: 1.5 }}>
              ⚠️ {errorCamara}
            </ManualLabel>
          ) : (
            <ManualLabel>Ingresá el interno del equipo:</ManualLabel>
          )}
          <ManualInput
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Ej: 342"
            onKeyDown={(e) => { if (e.key === 'Enter') enviarManual(); }}
            autoFocus
          />
          <Boton $primary type="button" onClick={enviarManual}>Buscar equipo</Boton>
          <Boton type="button" onClick={() => { detener(); setModoManual(false); }}>
            Escanear QR
          </Boton>
          <Boton type="button" onClick={() => { detener(); onCerrar(); }}>Cancelar</Boton>
        </ManualWrap>
      )}
    </Overlay>
  );
}
